import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { Send } from "lucide-react";

const QUICK_ACTIONS = [
  { type: "risksuggestions", label: "Suggest Risks" },
  { type: "statusreport", label: "Draft Status Report" },
  { type: "golivereadiness", label: "Go-Live Readiness" },
];

export default function AiCopilot({ projectId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [genBusy, setGenBusy] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/ai/history`).then((r) => {
      const msgs = [];
      r.data.forEach((h) => {
        msgs.push({ role: "user", content: h.userMessage });
        msgs.push({ role: "assistant", content: h.assistantReply });
      });
      setMessages(msgs);
    }).catch(() => {});
  }, [projectId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const history = messages.slice(-10);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        credentials: "include",
        body: JSON.stringify({ message: text, history }),
      });
      if (!res.ok) throw new Error(`AI request failed (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const evt = JSON.parse(line.slice(6));
          if (evt.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + evt.delta };
              return copy;
            });
          }
          if (evt.error) throw new Error(evt.error);
        }
      }
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `Error: ${e.message}`, streaming: false };
        return copy;
      });
    }
    setStreaming(false);
  };

  const runGeneration = async (genType, label) => {
    setGenBusy(genType);
    setMessages((m) => [...m, { role: "user", content: `Generate: ${label}` }]);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/generate`, { generationType: genType });
      setMessages((m) => [...m, { role: "assistant", content: JSON.stringify(data.result, null, 2), structured: true, genType, result: data.result }]);
    } catch (e) {
      toast.error(errMsg(e));
      setMessages((m) => [...m, { role: "assistant", content: `Generation failed: ${errMsg(e)}` }]);
    }
    setGenBusy(null);
  };

  const applyRisks = async (risks) => {
    try {
      for (const r of risks) {
        await api.post(`/projects/${projectId}/artifacts/raid_items`, {
          type: r.type, description: r.description, impact: r.impact,
          priority: r.priority, responseAction: r.suggestedResponse, status: "Open",
        });
      }
      toast.success(`${risks.length} suggestions added to RAID log`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="ai-copilot-panel">
      <div className="px-4 py-2 border-b border-neutral-800 flex gap-2 flex-wrap">
        {QUICK_ACTIONS.map((a) => (
          <button key={a.type} data-testid={`ai-quick-${a.type}`} disabled={!!genBusy || streaming}
            onClick={() => runGeneration(a.type, a.label)}
            className="text-[11px] uppercase tracking-wide border border-neutral-700 px-2 py-1 hover:border-[#5B93F0] hover:text-[#5B93F0] disabled:opacity-40">
            {genBusy === a.type ? "Working…" : a.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
        {messages.length === 0 && (
          <div className="text-neutral-500 text-xs leading-relaxed">
            Ask anything about this project — schedule impact, risks, change requests, go-live readiness. The assistant only sees data for your tenant and this project.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`ai-msg-${m.role}`}>
            <div className="overline-label text-neutral-600 mb-1">{m.role === "user" ? "You" : "Copilot"}</div>
            {m.structured ? (
              <div>
                <pre className="font-mono2 text-[11px] bg-neutral-900 border border-neutral-800 p-3 overflow-x-auto whitespace-pre-wrap">{m.content}</pre>
                {m.genType === "risksuggestions" && Array.isArray(m.result) && (
                  <button data-testid="apply-risks-btn" onClick={() => applyRisks(m.result)}
                    className="mt-2 text-[11px] uppercase tracking-wide bg-[#1E6FE8] px-3 py-1.5 hover:bg-[#1557C6]">
                    Add {m.result.length} to RAID log
                  </button>
                )}
              </div>
            ) : (
              <div className={`whitespace-pre-wrap leading-relaxed ${m.streaming ? "blink-cursor" : ""} ${m.role === "user" ? "text-neutral-300" : "text-neutral-100"}`}>
                {m.content}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-neutral-800 p-3 flex gap-2">
        <input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask the copilot…"
          className="flex-1 bg-neutral-900 border border-neutral-700 px-3 py-2 text-sm focus:outline-none focus:border-[#5B93F0]" />
        <button data-testid="ai-chat-send-btn" onClick={send} disabled={streaming}
          className="bg-[#1E6FE8] px-3 hover:bg-[#1557C6] disabled:opacity-40"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
